import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { customersAPI, freeOffersAPI, freeOfferTemplatesAPI } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  FileText,
  Save,
  Loader2,
  BookTemplate,
  Copy,
  Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function FreeOfferCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "Freitext-Angebot",
    payment_days: 14,
    include_vat: true,
    vat_rate: 19,
    notes: "",
    show_item_prices: true
  });
  
  const [items, setItems] = useState([
    { description: "", quantity: 1, unit: "Stück", unit_price: 0 }
  ]);

  // Template save dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersRes, templatesRes] = await Promise.all([
        customersAPI.list(),
        freeOfferTemplatesAPI.list()
      ]);
      setCustomers(customersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unit: "Stück", unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price);
    }, 0);
    const vatAmount = formData.include_vat ? subtotal * (formData.vat_rate / 100) : 0;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error("Bitte wählen Sie einen Kunden aus");
      return;
    }
    
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error("Bitte füllen Sie alle Positionen aus (Beschreibung und Preis)");
      return;
    }
    
    setSaving(true);
    try {
      const response = await freeOffersAPI.create({
        ...formData,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0
        }))
      });
      
      toast.success(`Freitext-Angebot ${response.data.offer_number} erstellt`);
      // Navigate to the detail page of the new offer
      navigate(`/freitext-angebote/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create offer:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Angebots");
    } finally {
      setSaving(false);
    }
  };

  const loadTemplate = (template) => {
    setFormData({
      ...formData,
      title: template.title,
      payment_days: template.payment_days,
      include_vat: template.include_vat,
      vat_rate: template.vat_rate,
      notes: template.notes || ""
    });
    setItems(template.items.map(item => ({
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price
    })));
    toast.success(`Vorlage "${template.name}" geladen`);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Bitte geben Sie einen Namen für die Vorlage ein");
      return;
    }
    
    if (items.some(item => !item.description)) {
      toast.error("Bitte füllen Sie alle Beschreibungen aus");
      return;
    }
    
    setSavingTemplate(true);
    try {
      await freeOfferTemplatesAPI.create({
        name: templateName,
        title: formData.title,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        payment_days: formData.payment_days,
        include_vat: formData.include_vat,
        vat_rate: formData.vat_rate,
        notes: formData.notes || null
      });
      
      toast.success(`Vorlage "${templateName}" gespeichert`);
      setTemplateDialogOpen(false);
      setTemplateName("");
      
      // Reload templates
      const templatesRes = await freeOfferTemplatesAPI.list();
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error("Failed to save template:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern der Vorlage");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Vorlage "${templateName}" wirklich löschen?`)) {
      return;
    }
    
    try {
      await freeOfferTemplatesAPI.delete(templateId);
      toast.success("Vorlage gelöscht");
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (error) {
      toast.error("Fehler beim Löschen der Vorlage");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const { subtotal, vatAmount, total } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">Freitext-Angebot erstellen</h1>
            <p className="text-muted-foreground">Erstellen Sie ein Angebot mit freien Positionen</p>
          </div>
        </div>
        
        {/* Template Actions */}
        <div className="flex items-center gap-2">
          {templates.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="load-template-btn">
                  <BookTemplate className="h-4 w-4 mr-2" />
                  Vorlage laden
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Gespeicherte Vorlagen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {templates.map((template) => (
                  <DropdownMenuItem
                    key={template.id}
                    className="flex justify-between items-center"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span 
                      className="flex-1 cursor-pointer hover:text-primary"
                      onClick={() => loadTemplate(template)}
                    >
                      {template.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTemplate(template.id, template.name);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          <Button 
            variant="outline" 
            onClick={() => setTemplateDialogOpen(true)}
            data-testid="save-template-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            Als Vorlage speichern
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Kunde</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Kunde auswählen *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                >
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.first_name} {customer.last_name}
                        {customer.company_name && ` (${customer.company_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="z.B. Wartungsarbeiten"
                />
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Positionen
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Position hinzufügen
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-4">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-muted-foreground">
                      Position {index + 1}
                    </span>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Beschreibung *</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => updateItem(index, "description", e.target.value)}
                      placeholder="Beschreibung der Leistung/des Artikels"
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Menge</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Einheit</Label>
                      <Select
                        value={item.unit}
                        onValueChange={(value) => updateItem(index, "unit", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Stück">Stück</SelectItem>
                          <SelectItem value="Stunden">Stunden</SelectItem>
                          <SelectItem value="Pauschal">Pauschal</SelectItem>
                          <SelectItem value="m²">m²</SelectItem>
                          <SelectItem value="lfm">lfm</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Einzelpreis (€) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="text-right text-sm">
                    <span className="text-muted-foreground">Summe: </span>
                    <span className="font-medium">{formatCurrency(item.quantity * item.unit_price)}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Optionen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Zahlungsziel (Tage)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.payment_days}
                    onChange={(e) => setFormData({ ...formData, payment_days: parseInt(e.target.value) || 14 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>MwSt.-Satz (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.vat_rate}
                    onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) || 19 })}
                    disabled={!formData.include_vat}
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="include_vat"
                  checked={formData.include_vat}
                  onCheckedChange={(checked) => setFormData({ ...formData, include_vat: checked })}
                />
                <Label htmlFor="include_vat">Mit Mehrwertsteuer</Label>
              </div>

              <div className="flex items-start space-x-2 p-3 rounded-md bg-muted/30 border">
                <Switch
                  id="show_item_prices"
                  checked={formData.show_item_prices}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_item_prices: checked })}
                  data-testid="show-item-prices-switch"
                />
                <div>
                  <Label htmlFor="show_item_prices">Einzelpreise im PDF ausweisen</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Aus → Im PDF werden nur Pos./Beschreibung/Menge gezeigt; die Gesamtsumme erscheint am Ende.
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Anmerkungen</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Zusätzliche Informationen..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Price Overview */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg">Preisübersicht</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nettobetrag</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              
              {formData.include_vat && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MwSt. ({formData.vat_rate}%)</span>
                  <span>{formatCurrency(vatAmount)}</span>
                </div>
              )}
              
              <Separator />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamtbetrag {formData.include_vat ? "(Brutto)" : "(Netto)"}</span>
                <span className="text-primary">{formatCurrency(total)}</span>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={saving}
                data-testid="create-offer-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Angebot erstellen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </form>

      {/* Save Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Als Vorlage speichern</DialogTitle>
            <DialogDescription>
              Speichern Sie das aktuelle Angebot als wiederverwendbare Vorlage.
              Die Kundenauswahl wird nicht gespeichert.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Vorlagenname *</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="z.B. Standard-Beratung"
                data-testid="template-name-input"
              />
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p><strong>Gespeicherte Daten:</strong></p>
              <ul className="list-disc list-inside mt-1">
                <li>Titel: {formData.title}</li>
                <li>{items.length} Position(en)</li>
                <li>Zahlungsziel: {formData.payment_days} Tage</li>
                <li>MwSt.: {formData.include_vat ? `${formData.vat_rate}%` : "Ohne"}</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={saveAsTemplate} 
              disabled={savingTemplate}
              data-testid="confirm-save-template-btn"
            >
              {savingTemplate ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichert...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Vorlage speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
